import { useCallback, useRef, useState } from "react";
import * as tus from "tus-js-client";

import { discardUpload } from "../lib/tusUpload";

/** 사용자가 업로드를 중지했을 때 업로드 루프를 빠져나오기 위한 에러 */
export class UploadCancelledError extends Error {
  constructor() {
    super("업로드가 중지되었습니다.");
    this.name = "UploadCancelledError";
  }
}

export const isUploadCancelled = (e: unknown): e is UploadCancelledError =>
  e instanceof UploadCancelledError;

interface ActiveUpload {
  upload: tus.Upload;
  reject: (e: unknown) => void;
  /** 파일 바이트를 모두 보냈는지 (보낸 뒤에는 서버가 슬라이드로 등록하므로 건드리지 않는다) */
  sent: boolean;
}

/**
 * TUS 슬라이드 업로드 중지 컨트롤러.
 *
 * 사용법 (파일을 순서대로 올리는 루프 기준)
 * - 업로드 시작 전 `begin()`
 * - 각 파일 시작 전 `throwIfCancelled()`
 * - `new tus.Upload(...)` 직후 `track(upload, reject)`
 * - onProgress 에서 바이트를 다 보냈으면 `markSent(upload)`
 * - catch 에서 `isUploadCancelled(error)` 로 중지와 오류를 구분
 *
 * 중지 규칙 (안전 우선)
 * - 전송 중인 파일: 요청을 끊고 서버의 미완료 업로드를 삭제한다.
 * - 이미 다 보낸 파일: 서버가 슬라이드로 등록 중이므로 건드리지 않고, 다음 파일부터 멈춘다.
 * - 이미 등록된 슬라이드/게시물은 자동으로 지우지 않는다.
 */
export const useTusCancel = () => {
  const activeRef = useRef<ActiveUpload | null>(null);
  const cancelledRef = useRef(false);
  const [cancelling, setCancelling] = useState(false);

  const begin = useCallback(() => {
    cancelledRef.current = false;
    activeRef.current = null;
    setCancelling(false);
  }, []);

  const track = useCallback((upload: tus.Upload, reject: (e: unknown) => void) => {
    activeRef.current = { upload, reject, sent: false };
  }, []);

  const markSent = useCallback((upload: tus.Upload) => {
    if (activeRef.current?.upload === upload) activeRef.current.sent = true;
  }, []);

  const throwIfCancelled = useCallback(() => {
    if (cancelledRef.current) throw new UploadCancelledError();
  }, []);

  /** start() 전에 비동기 대기(findPreviousUploads 등)가 있을 때, 그 사이 중지됐는지 확인 */
  const isCancelled = useCallback(() => cancelledRef.current, []);

  const cancel = useCallback(async () => {
    if (cancelledRef.current) return;
    cancelledRef.current = true;
    setCancelling(true);

    const active = activeRef.current;
    activeRef.current = null;
    if (!active || active.sent) return;

    await active.upload.abort(false).catch(() => undefined);
    active.reject(new UploadCancelledError());
    await discardUpload(active.upload);
  }, []);

  return { cancelling, begin, track, markSent, throwIfCancelled, isCancelled, cancel };
};
